/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 2 React.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 2 React using thie file.
 */

const colors = {
  background: {
    default: "#f0f2f5",
  },

  text: {
    main: "#7b809a",
    focus: "#7b809a",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  // Este es el color primario de la aplicación. Se utiliza para elementos principales como botones destacados, el Sidenav
  // cuando se establece como 'primary', y otros componentes de UI importantes. Actualmente está configurado
  // con el morado del logo de Quadrante H.
  primary: {
    main: "#9C27B0", // Tono principal morado
    focus: "#7B1FA2", // Tono morado para el estado de enfoque
  },

  // Color secundario, utilizado para elementos menos prominentes o de contraste.
  secondary: {
    main: "#626a79",
    focus: "#747b8a",
  },

  // Este color se utiliza para información general o elementos que requieren un color distintivo.
  // Anteriormente era azul, ahora está configurado con el morado del logo de Quadrante H
  // para una mayor consistencia de marca. Esto afecta a los encabezados de las tarjetas
  // y otros componentes que usen el color 'info'.
  info: {
    main: "#9C27B0", // Tono principal morado (anteriormente un tono de azul)
    focus: "#7B1FA2", // Tono morado para el estado de enfoque (anteriormente un tono de azul)
  },

  // Color de éxito, utilizado para indicar operaciones completadas o estados positivos.
  // Se mantiene el verde del logo de Quadrante H.
  success: {
    main: "#4CAF50",
    focus: "#388E3C",
  },

  // Color de advertencia, utilizado para notificaciones o estados que requieren atención.
  warning: {
    main: "#fb8c00",
    focus: "#fc9d26",
  },

  // Color de error, utilizado para indicar fallos o estados negativos.
  error: {
    main: "#F44335",
    focus: "#f65f53",
  },

  // Color claro, a menudo utilizado para fondos o elementos de UI suaves.
  light: {
    main: "#f0f2f5",
    focus: "#f0f2f5",
  },

  // Color oscuro, utilizado para fondos o elementos de texto en contraste.
  dark: {
    main: "#2C2C2C",
    focus: "#2C2C2C",
  },

  // El color morado específico del logo de Quadrante H. Se utiliza como referencia para
  // 'primary' y 'info' en la paleta.
  purple: {
    main: "#9C27B0",
    focus: "#7B1FA2",
  },

  // Tonos de gris para diversos elementos de UI.
  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  // Los gradientes de color se utilizan para fondos de componentes con 'variant="gradient"'.
  // Cada propiedad aquí define el color inicial ('main') y final ('state') del gradiente.
  gradients: {
    // El gradiente primario ahora utiliza los tonos morados del logo.
    primary: {
      main: "#AB47BC", // Inicio del gradiente morado
      state: "#9C27B0", // Fin del gradiente morado
    },

    secondary: {
      main: "#747b8a",
      state: "#626a79",
    },

    // El gradiente de información (info) ahora también utiliza los tonos morados del logo.
    info: {
      main: "#AB47BC", // Inicio del gradiente morado (anteriormente azul)
      state: "#9C27B0", // Fin del gradiente morado (anteriormente azul)
    },

    // El gradiente de éxito se mantiene con los tonos verdes del logo.
    success: {
      main: "#66BB6A",
      state: "#4CAF50",
    },

    warning: {
      main: "#FFA726",
      state: "#FB8C00",
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: "#EBEFF4",
      state: "#CED4DA",
    },

    dark: {
      main: "#42424a",
      state: "#191919",
    },

    // Gradiente morado que sirve como base para 'primary' e 'info'.
    purple: {
      main: "#AB47BC",
      state: "#9C27B0",
    },
  },

  // Colores para iconos de redes sociales. No se han modificado en este proceso.
  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  // Colores utilizados para badges (insignias) en la UI. No se han modificado en este proceso.
  badgeColors: {
    primary: {
      background: "#f8b3ca",
      text: "#cc084b",
    },

    secondary: {
      background: "#d7d9e1",
      text: "#626a79",
    },

    info: {
      background: "#aecef7",
      text: "#095bc6",
    },

    success: {
      background: "#bce2be",
      text: "#339537",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  // Define las sombras de color que se aplican a los componentes con 'coloredShadow'.
  coloredShadows: {
    primary: "#9C27B0", // La sombra primaria ahora es morada.
    secondary: "#626a79",
    info: "#9C27B0", // La sombra 'info' ahora es morada (anteriormente verde).
    success: "#4CAF50",
    warning: "#ff9900",
    error: "#f44336",
    light: "#adb5bd",
    dark: "#2C2C2C",
  },

  // Color del borde para los campos de entrada. No se ha modificado.
  inputBorderColor: "#d2d6da",

  // Estilos para los indicadores de las pestañas (tabs). No se han modificado.
  tabs: {
    indicator: { boxShadow: "#ddd" },
  },
};

export default colors;
